import { Bitmap, gridSprite } from "./bitmap.js";
import { COLORS, HAIR_COLORS, SHIRT_COLORS, SKIN, shade, type Color } from "./palette.js";

/** Same shape as Look in @squire/core (sprites stay independent of core). */
export interface CharacterLook {
  skin: number;
  hair: number;
  hairColor: number;
  beard: number;
  glasses: boolean;
  shirt: number;
}

/** Limb offsets relative to its joint origin: [elbowX, elbowY, handX, handY] (or knee/foot). */
export type Limb = readonly [number, number, number, number];

export type PropId =
  | "book"
  | "laptop"
  | "notebook"
  | "broom"
  | "vacuum"
  | "bag_trash"
  | "bag_shop"
  | "dumbbell"
  | "pan"
  | "bowl"
  | "mug"
  | "box"
  | "controller"
  | "guitar"
  | "beer";

export type OverlayId =
  "question" | "dots1" | "dots2" | "dots3" | "zz" | "spark" | "note" | "excl" | "heart" | "steam";

export interface PropPlacement {
  id: PropId;
  at: "handL" | "handR" | "hands" | "ground" | "lap";
  dx?: number;
  dy?: number;
}

/** One animation frame as data. All fields optional, defaults = standing front pose. */
export interface Pose {
  /** Whole body shift (positive y = down). */
  dx?: number;
  dy?: number;
  headDx?: number;
  headDy?: number;
  /** Shoulders and head 1px down (breathing). */
  breath?: boolean;
  armL?: Limb;
  armR?: Limb;
  legL?: Limb;
  legR?: Limb;
  eyes?: "open" | "closed" | "left" | "right" | "up" | "down";
  mouth?: "closed" | "open" | "smile" | "o";
  /** Upright (default), side-view lying face down (push-ups), lying on the back (sleep), sitting. */
  orient?: "up" | "prone" | "supine" | "sit" | "floor";
  /** Prone only: arm extension 0..6. */
  raise?: number;
  back?: boolean;
  props?: readonly PropPlacement[];
  overlay?: OverlayId;
  headphones?: boolean;
  blanket?: boolean;
  /** Draw nothing (behind the door). */
  hidden?: boolean;
}

export interface CharacterOptions {
  /** Reading glasses while reading (Mind lv 3+, when the character doesn't wear glasses). */
  readingGlasses?: boolean;
}

export const BODY_STAGES = [
  { torsoW: 6, armW: 1, legW: 2, chest: false, delts: false },
  { torsoW: 7, armW: 2, legW: 2, chest: false, delts: false },
  { torsoW: 8, armW: 2, legW: 2, chest: true, delts: false },
  { torsoW: 9, armW: 2, legW: 3, chest: true, delts: true },
  { torsoW: 10, armW: 3, legW: 3, chest: true, delts: true },
] as const;

export const CHAR_W = 48;
export const CHAR_H = 50;
export const CHAR_OX = 24;
export const CHAR_OY = 46;

const PANTS = COLORS.blueDark;
const SHOES = COLORS.brown;
const DEFAULT_ARM: Limb = [0, 5, 0, 9];
const DEFAULT_LEG: Limb = [0, 4, 0, 8];

interface Ctx {
  bm: Bitmap;
  skin: Color;
  skinDark: Color;
  hair: Color;
  shirt: Color;
  look: CharacterLook;
  opts: CharacterOptions;
}

/** Compose one frame of the avatar into a bitmap (origin = feet centre on the ground line). */
export function composeCharacter(
  look: CharacterLook,
  stageIndex: number,
  pose: Pose,
  opts: CharacterOptions = {},
): Bitmap {
  const bm = new Bitmap(CHAR_W, CHAR_H, CHAR_OX, CHAR_OY);
  if (pose.hidden) return bm;
  const skin = SKIN[look.skin] ?? COLORS.skin1;
  const ctx: Ctx = {
    bm,
    skin,
    skinDark: shade(skin),
    hair: HAIR_COLORS[look.hairColor] ?? COLORS.brown,
    shirt: SHIRT_COLORS[look.shirt] ?? COLORS.blue,
    look,
    opts,
  };
  const stage = BODY_STAGES[Math.max(0, Math.min(4, stageIndex))]!;
  const orient = pose.orient ?? "up";
  if (orient === "prone") drawProne(ctx, stage, pose);
  else if (orient === "supine") drawSupine(ctx, pose);
  else drawUpright(ctx, stage, pose, orient);
  bm.outline(COLORS.ink);
  return bm;
}

function drawUpright(ctx: Ctx, st: (typeof BODY_STAGES)[number], pose: Pose, orient: "up" | "sit" | "floor") {
  const { bm } = ctx;
  const dx = pose.dx ?? 0;
  const sitDrop = orient === "sit" ? 5 : orient === "floor" ? 8 : 0;
  const dy = (pose.dy ?? 0) + sitDrop;
  const b = pose.breath ? 1 : 0;
  const W = st.torsoW;
  const x0 = dx - Math.floor(W / 2);
  const hipY = -10 + dy;
  const shoulderY = -21 + dy + b;
  const headTop = -30 + dy + b + (pose.headDy ?? 0);
  const headX = dx - 4 + (pose.headDx ?? 0);

  // Back hair (long hair, ponytail) goes behind everything.
  if (!pose.back) drawHairBack(ctx, headX, headTop);

  // Legs.
  const legT = st.legW;
  const hipL = x0 + 1;
  const hipR = x0 + W - legT - (legT === 3 ? -1 : 0) - 1;
  if (orient === "floor") {
    // Cross-legged on the floor: knees out, shins crossed.
    bm.line(hipL, hipY + 1, hipL - 4, -2, 2, PANTS);
    bm.line(hipR + 1, hipY + 1, hipR + 5, -2, 2, PANTS);
    bm.line(hipL - 4, -1, hipR + 5, -1, 2, PANTS);
    bm.rect(hipL - 5, -1, 2, 1, SHOES);
    bm.rect(hipR + 5, -1, 2, 1, SHOES);
  } else {
    for (const [hx, limb, out] of [
      [hipL, pose.legL ?? DEFAULT_LEG, -1],
      [hipR, pose.legR ?? DEFAULT_LEG, 1],
    ] as const) {
      const kx = hx + limb[0];
      const ky = Math.min(-2, hipY + limb[1]);
      const fx = hx + limb[2];
      const ty = Math.min(-2, hipY + limb[3]);
      bm.line(hx, hipY, kx, ky, legT, PANTS);
      bm.line(kx, ky, fx, ty, legT, PANTS);
      const shoeX = out < 0 ? fx - 1 : fx;
      bm.rect(shoeX, ty + 1, legT + 1, 2, SHOES);
    }
  }

  // Torso.
  const torsoColor = ctx.shirt;
  bm.rect(x0, shoulderY, W, hipY - shoulderY, torsoColor);
  bm.rect(x0 + W - 1, shoulderY + 1, 1, hipY - shoulderY - 1, shade(torsoColor));
  if (st.delts) {
    bm.rect(x0 - 1, shoulderY, 1, 2, torsoColor);
    bm.rect(x0 + W, shoulderY, 1, 2, shade(torsoColor));
  }
  if (st.chest && !pose.back) {
    bm.rect(dx - 1, shoulderY + 3, 1, 1, shade(torsoColor));
    bm.rect(x0 + 1, shoulderY + 4, W - 2, 1, shade(torsoColor));
    bm.rect(dx - 1, shoulderY + 3, 2, 1, shade(torsoColor));
  }
  bm.rect(x0, hipY, W, 1, PANTS);
  if (pose.blanket) {
    bm.rect(x0 - 3, shoulderY + 1, W + 6, -shoulderY - 1, COLORS.purple);
    for (let i = 0; i < W + 6; i += 3) bm.rect(x0 - 3 + i, shoulderY + 3, 1, 1, COLORS.pink);
  }

  // Arms.
  const armT = st.armW;
  const shL = x0 - (armT === 1 ? 1 : 2);
  const shR = x0 + W + (armT === 3 ? 1 : 0);
  const sY = shoulderY + 1;
  const hands: { L: [number, number]; R: [number, number] } = { L: [shL, sY + 9], R: [shR, sY + 9] };
  for (const side of ["L", "R"] as const) {
    const limb = (side === "L" ? pose.armL : pose.armR) ?? DEFAULT_ARM;
    const sx = side === "L" ? shL : shR;
    const ex = sx + limb[0];
    const ey = sY + limb[1];
    const hx = sx + limb[2];
    const hy = sY + limb[3];
    if (!pose.blanket) {
      bm.line(sx, sY, ex, ey, armT, torsoColor);
      bm.line(ex, ey, hx, hy, armT, ctx.skin);
    }
    hands[side] = [hx, hy];
  }

  // Head.
  drawHead(ctx, headX, headTop, pose);

  // Props.
  for (const p of pose.props ?? []) {
    let px: number;
    let py: number;
    if (p.at === "hands") {
      px = Math.round((hands.L[0] + hands.R[0]) / 2);
      py = Math.round((hands.L[1] + hands.R[1]) / 2);
    } else if (p.at === "handL" || p.at === "handR") {
      [px, py] = hands[p.at === "handL" ? "L" : "R"];
    } else if (p.at === "lap") {
      px = dx;
      py = hipY;
    } else {
      px = dx;
      py = 0;
    }
    bm.blit(PROPS[p.id], px + (p.dx ?? 0), py + (p.dy ?? 0));
  }
}

function drawHead(ctx: Ctx, x: number, top: number, pose: Pose) {
  const { bm, skin } = ctx;
  bm.rect(x, top + 1, 8, 7, skin);
  bm.rect(x + 1, top, 6, 1, skin);
  bm.rect(x + 7, top + 2, 1, 5, ctx.skinDark);
  if (pose.back) {
    if (ctx.look.hair !== 2) {
      bm.rect(x, top, 8, 7, ctx.hair);
      bm.rect(x + 1, top - 1, 6, 1, ctx.hair);
      bm.rect(x + 7, top + 1, 1, 6, shade(ctx.hair));
    }
    if (pose.headphones) drawHeadphones(ctx, x, top);
    return;
  }
  // Ears.
  bm.set(x - 1, top + 4, skin);
  bm.set(x + 8, top + 4, ctx.skinDark);

  const ey = top + 4;
  const eyes = pose.eyes ?? "open";
  const ex = eyes === "left" ? -1 : eyes === "right" ? 1 : 0;
  const eyY = eyes === "up" ? -1 : eyes === "down" ? 1 : 0;
  const glasses = ctx.look.glasses || ctx.opts.readingGlasses;

  // Beard below eyes.
  const beard = ctx.look.beard;
  if (beard === 1) {
    for (const bx of [0, 2, 5, 7]) bm.set(x + bx, top + 7, ctx.skinDark);
    for (const bx of [1, 6]) bm.set(x + bx, top + 6, ctx.skinDark);
  } else if (beard === 3) {
    bm.rect(x, top + 5, 1, 3, ctx.hair);
    bm.rect(x + 7, top + 5, 1, 3, ctx.hair);
    bm.rect(x + 1, top + 6, 6, 2, ctx.hair);
    bm.rect(x + 2, top + 8, 4, 1, ctx.hair);
  }

  if (glasses) {
    bm.rect(x + 1, ey - 1, 6, 1, COLORS.ink);
    bm.rect(x + 1, ey, 3, 1, COLORS.greyLight);
    bm.rect(x + 4, ey, 3, 1, COLORS.greyLight);
  }
  if (eyes === "closed") {
    bm.rect(x + 2, ey, 1, 1, COLORS.ink);
    bm.rect(x + 5, ey, 1, 1, COLORS.ink);
    bm.set(x + 1, ey, ctx.skinDark);
    bm.set(x + 6, ey, ctx.skinDark);
  } else {
    bm.set(x + 2 + ex, ey + eyY, COLORS.ink);
    bm.set(x + 5 + ex, ey + eyY, COLORS.ink);
  }

  // Mouth.
  const my = top + 6;
  const mouth = pose.mouth ?? "closed";
  if (beard === 2) bm.rect(x + 2, my - 1, 4, 1, ctx.hair);
  if (mouth === "open") {
    bm.rect(x + 3, my, 2, 2, COLORS.redDark);
  } else if (mouth === "o") {
    bm.rect(x + 3, my, 2, 1, COLORS.ink);
  } else if (mouth === "smile") {
    bm.rect(x + 3, my + 1, 2, 1, COLORS.redDark);
    bm.set(x + 2, my, COLORS.redDark);
    bm.set(x + 5, my, COLORS.redDark);
  } else {
    bm.rect(x + 3, my, 2, 1, beard === 3 ? COLORS.redDark : ctx.skinDark);
  }

  drawHairFront(ctx, x, top);
  if (pose.headphones) drawHeadphones(ctx, x, top);
}

function drawHeadphones(ctx: Ctx, x: number, top: number) {
  const { bm } = ctx;
  bm.rect(x, top - 2, 8, 1, COLORS.greyDark);
  bm.set(x - 1, top - 1, COLORS.greyDark);
  bm.set(x + 8, top - 1, COLORS.greyDark);
  bm.rect(x - 2, top + 3, 2, 3, COLORS.red);
  bm.rect(x + 8, top + 3, 2, 3, COLORS.red);
}

function drawHairBack(ctx: Ctx, x: number, top: number) {
  const { bm, hair } = ctx;
  if (ctx.look.hair === 1) {
    bm.rect(x - 1, top + 2, 2, 10, hair);
    bm.rect(x + 7, top + 2, 2, 10, shade(hair));
  }
  if (ctx.look.hair === 5) {
    bm.rect(x + 8, top + 2, 2, 2, hair);
    bm.rect(x + 9, top + 4, 2, 5, shade(hair));
  }
}

function drawHairFront(ctx: Ctx, x: number, top: number) {
  const { bm, hair } = ctx;
  const hs = shade(hair);
  switch (ctx.look.hair) {
    case 0: // short
      bm.rect(x + 1, top - 1, 6, 1, hair);
      bm.rect(x, top, 8, 2, hair);
      bm.rect(x, top + 2, 1, 2, hair);
      bm.rect(x + 7, top + 2, 1, 2, hs);
      bm.rect(x + 5, top + 2, 2, 1, hair);
      break;
    case 1: // longer
      bm.rect(x + 1, top - 1, 6, 1, hair);
      bm.rect(x, top, 8, 2, hair);
      bm.rect(x - 1, top + 1, 1, 6, hair);
      bm.rect(x + 8, top + 1, 1, 6, hs);
      bm.rect(x, top + 2, 2, 1, hair);
      bm.rect(x + 6, top + 2, 2, 1, hs);
      break;
    case 2: // bald
      bm.set(x + 2, top + 1, COLORS.paper);
      break;
    case 3: // buzz
      bm.rect(x + 1, top, 6, 1, hair);
      bm.set(x, top + 1, hair);
      bm.set(x + 7, top + 1, hs);
      bm.rect(x + 1, top + 1, 6, 1, hs);
      break;
    case 4: // curly
      for (const cx of [1, 3, 5]) bm.rect(x + cx, top - 2, 2, 1, hair);
      bm.rect(x - 1, top - 1, 10, 2, hair);
      bm.rect(x - 1, top + 1, 2, 4, hair);
      bm.rect(x + 7, top + 1, 2, 4, hs);
      bm.set(x + 2, top + 1, hs);
      bm.set(x + 5, top + 1, hs);
      bm.rect(x + 2, top + 2, 1, 1, hair);
      break;
    case 5: // ponytail / bun
      bm.rect(x + 1, top - 1, 6, 1, hair);
      bm.rect(x, top, 8, 2, hair);
      bm.rect(x, top + 2, 1, 2, hair);
      bm.rect(x + 7, top + 2, 1, 2, hs);
      bm.rect(x + 3, top - 3, 3, 2, hs);
      break;
  }
}

function drawProne(ctx: Ctx, st: (typeof BODY_STAGES)[number], pose: Pose) {
  const { bm } = ctx;
  const r = Math.max(0, Math.min(6, pose.raise ?? 4));
  const dx = pose.dx ?? 0;
  const t = st.torsoW >= 9 ? 4 : 3;
  const shY = -r - 3;
  const hipY = -Math.round(r / 2) - 3;
  // Arms straight down to the floor.
  bm.line(dx - 7, shY + 1, dx - 7, -1, st.armW + 1, ctx.skin);
  bm.line(dx - 7, shY, dx - 7, shY + 2, st.armW + 1, ctx.shirt);
  // Legs to the toes.
  bm.line(dx + 3, hipY, dx + 13, -2, 2, PANTS);
  bm.rect(dx + 13, -2, 2, 2, SHOES);
  // Torso.
  bm.line(dx - 8, shY, dx + 3, hipY, t, ctx.shirt);
  // Side head.
  const hy = shY - 6;
  const hx = dx - 15;
  bm.rect(hx, hy, 7, 6, ctx.skin);
  bm.set(hx + 1, hy + 2, COLORS.ink);
  bm.set(hx, hy + 4, ctx.skinDark);
  if (ctx.look.hair !== 2) {
    bm.rect(hx + 1, hy - 1, 6, 2, ctx.hair);
    bm.rect(hx + 5, hy, 2, 4, ctx.hair);
  }
  if (ctx.look.beard === 3) bm.rect(hx + 1, hy + 4, 4, 2, ctx.hair);
}

function drawSupine(ctx: Ctx, pose: Pose) {
  const { bm } = ctx;
  const dx = pose.dx ?? 0;
  // Head on the pillow (left), face up-ish, eyes closed.
  const hx = dx - 15;
  const hy = -9 + (pose.dy ?? 0);
  bm.rect(hx, hy, 8, 7, ctx.skin);
  if (ctx.look.hair !== 2) {
    bm.rect(hx - 1, hy, 2, 6, ctx.hair);
    bm.rect(hx, hy - 1, 6, 2, ctx.hair);
  }
  bm.rect(hx + 3, hy + 3, 2, 1, COLORS.ink);
  bm.rect(hx + 6, hy + 3, 1, 1, COLORS.ink);
  bm.set(hx + 5, hy + 5, ctx.skinDark);
  if (ctx.look.beard === 3) bm.rect(hx + 3, hy + 5, 5, 2, ctx.hair);
  // Blanket over the body.
  const by = -7 + (pose.dy ?? 0) + (pose.breath ? 1 : 0);
  bm.rect(dx - 7, by, 22, -by - 1, COLORS.purple);
  bm.rect(dx - 7, by, 22, 1, COLORS.pink);
  bm.rect(dx + 12, by - 1, 3, 1, COLORS.purple);
  for (let i = 0; i < 20; i += 4) bm.set(dx - 5 + i, by + 3, COLORS.pink);
}

/** Small prop sprites. Origin = the point that sits in the hand (or on the ground). */
export const PROPS: Record<PropId, Bitmap> = {
  book: gridSprite(["rpppppr", "rpkppkr", "rrrrrrr"], "top-left"),
  laptop: gridSprite([".gggggg.", ".gsssss.", ".gsssss.", "aaaaaaaa", "gggggggg"]),
  notebook: gridSprite(["...y", "ppy.", "ppp.", "ppp."]),
  broom: gridSprite(["m", "m", "m", "m", "m", "m", "m", "m", "m", "m", "yyy", "yyy", "y.y"], "top-left"),
  vacuum: gridSprite(
    ["g.....", "g.....", "g.....", "g.....", "g.....", "grrrr.", "rrrrrr", "rrrrrr", "k.k..k"],
    "top-left",
  ),
  bag_trash: gridSprite([".k.", "GGG", "GGGG", "GGGG", "GGGG", ".GG."], "top-left"),
  bag_shop: gridSprite(["e.e.", "cccc", "cccc", "cccc", "cccc"], "top-left"),
  dumbbell: gridSprite(["k.k", "kgk", "k.k"], "top-left"),
  pan: gridSprite(["GGGGmm", ".GG..."], "top-left"),
  bowl: gridSprite(["...a", "cccc", ".cc."], "top-left"),
  mug: gridSprite(["hh.", "hhh", "hh."], "top-left"),
  box: gridSprite(
    ["llllllllll", "lwwwyywwwl", "lwwwyywwwl", "lwwwwwwwwl", "lwwwwwwwwl", "lwwwwwwwwl", "llllllllll"],
    "top-left",
  ),
  controller: gridSprite(["GGGG", "GkkG"], "top-left"),
  guitar: gridSprite(["m.....", ".m....", "..m...", "...ooo", "..oomoo", "..ooooo", "...ooo."], "top-left"),
  beer: gridSprite(["hhh.", "yYyg", "yyyg", "yyy."], "top-left"),
};

/** Overlay bubbles above the head. Origin = bottom-left. */
export const OVERLAYS: Record<OverlayId, Bitmap> = {
  question: gridSprite([".hhh.", "h...h", "...h.", "..h..", ".....", "..h.."]),
  dots1: gridSprite(["h...."]),
  dots2: gridSprite(["h.h.."]),
  dots3: gridSprite(["h.h.h"]),
  zz: gridSprite(["...hhh", "....h.", "...hhh", "hhh...", ".h....", "hhh..."]),
  spark: gridSprite(["..y..", ".yYy.", "yYhYy", ".yYy.", "..y.."]),
  note: gridSprite(["..hh", "..h.", "..h.", "hhh.", "hh.."]),
  excl: gridSprite(["y", "y", "y", ".", "y"]),
  heart: gridSprite([".r.r.", "rrrrr", ".rrr.", "..r.."]),
  steam: gridSprite([".a..", "..a.", ".a..", "a..."]),
};
